import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, Settings2, Coins, Hand, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createWorld3D, autoTier } from "./scene3d";

const TODOS = [
  { todo: "bite", label: "Grab a bite" },
  { todo: "groceries", label: "Buy groceries" },
  { todo: "catfood", label: "Buy cat food" },
  { todo: "drink", label: "Get a drink" },
  { todo: "dress", label: "Snag a dress" },
];
const QORDER = ["high", "medium", "low"];
const QLABEL = { high: "High", medium: "Medium", low: "Low" };
const isTouch = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

export default function World3D({ onExit }) {
  const holderRef = useRef(null);
  const worldRef = useRef(null);
  const [coins, setCoins] = useState(() => Number(localStorage.getItem("cq_city_coins") || 0));
  const [done, setDone] = useState({});
  const [near, setNear] = useState(null);
  const [quality, setQuality] = useState(() => { try { return localStorage.getItem("cq_3d_quality") || autoTier(); } catch { return "high"; } });
  const [loading, setLoading] = useState(true);
  const doneRef = useRef(done); doneRef.current = done;
  const coinsRef = useRef(coins); coinsRef.current = coins;

  const award = useCallback((n) => { setCoins((c) => { const v = c + n; try { localStorage.setItem("cq_city_coins", String(v)); } catch {} return v; }); }, []);

  const handleInteract = useCallback((entry) => {
    if (!entry) return;
    if (doneRef.current[entry.todo]) { toast(`✓ Already did: ${entry.label.split("—")[1]?.trim() || entry.label}`); return; }
    setDone((d) => {
      const nd = { ...d, [entry.todo]: true };
      const all = TODOS.every((t) => nd[t.todo]);
      award(entry.coins + (all ? 25 : 0));
      toast.success(all ? `🎉 All errands done! +${entry.coins} +25 bonus coins` : `+${entry.coins} coins · ${entry.label.split("—")[1]?.trim() || entry.label}`);
      return nd;
    });
  }, [award]);

  useEffect(() => {
    const holder = holderRef.current;
    const world = createWorld3D({
      container: holder, quality,
      onProximity: (e) => setNear(e),
      onInteract: (e) => handleInteract(e),
    });
    worldRef.current = world; world.start();
    const t = setTimeout(() => setLoading(false), 700);
    return () => { clearTimeout(t); world.dispose(); worldRef.current = null; };
    // eslint-disable-line
  }, []); // eslint-disable-line

  const cycleQuality = () => { const i = QORDER.indexOf(quality); const q = QORDER[(i + 1) % QORDER.length]; setQuality(q); try { localStorage.setItem("cq_3d_quality", q); } catch {} worldRef.current?.setQuality(q); };

  // ---- Mobile joystick ----
  const joyRef = useRef(null); const [knob, setKnob] = useState({ x: 0, y: 0 }); const joyId = useRef(null);
  const joyMove = (e) => {
    if (joyId.current !== e.pointerId) return;
    const r = joyRef.current.getBoundingClientRect(); const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    let dx = e.clientX - cx, dy = e.clientY - cy; const max = r.width / 2; const d = Math.hypot(dx, dy);
    if (d > max) { dx = dx / d * max; dy = dy / d * max; }
    setKnob({ x: dx, y: dy });
    worldRef.current?.setMoveAxis(dx / max, -dy / max);
  };
  const joyStart = (e) => { joyId.current = e.pointerId; e.currentTarget.setPointerCapture?.(e.pointerId); joyMove(e); };
  const joyEnd = (e) => { if (joyId.current !== e.pointerId) return; joyId.current = null; setKnob({ x: 0, y: 0 }); worldRef.current?.setMoveAxis(0, 0); };

  const actionText = near ? (near.label.split("—")[1]?.trim() || near.label) : "";

  return (
    <div className="fixed inset-0 z-[120] bg-[#0b1220] overflow-hidden select-none" data-testid="world3d" style={{ fontFamily: "Manrope, system-ui" }}>
      <div ref={holderRef} className="absolute inset-0" data-testid="world3d-canvas" />

      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-30 grid place-items-center bg-[#0b1220]" data-testid="world3d-loading">
          <div className="text-center">
            <Loader2 className="animate-spin text-cyan-300 mx-auto" size={34} />
            <p className="cq-px text-cyan-200 text-[11px] mt-4">ENTERING QUACK CITY 3D</p>
            <p className="text-slate-400 text-xs mt-2">Warming up the lights & materials…</p>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center gap-2 px-3 py-3 pointer-events-none">
        <button onClick={onExit} className="pointer-events-auto cq-px text-[9px] px-3 py-2 rounded-lg bg-black/45 backdrop-blur border border-white/15 text-slate-100 hover:bg-black/60 inline-flex items-center gap-1" data-testid="world3d-exit"><ChevronLeft size={13} /> 2D</button>
        <div className="pointer-events-auto flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/45 backdrop-blur border border-amber-300/30" data-testid="world3d-coins"><Coins size={13} className="text-amber-300" /><span className="cq-px text-[10px] text-amber-200">{coins}</span></div>
        <span className="flex-1" />
        <button onClick={cycleQuality} className="pointer-events-auto cq-px text-[8px] px-3 py-2 rounded-lg bg-black/45 backdrop-blur border border-white/15 text-slate-200 hover:bg-black/60 inline-flex items-center gap-1.5" data-testid="world3d-quality"><Settings2 size={12} /> {QLABEL[quality]}</button>
        <span className="pointer-events-auto px-2.5 py-2 rounded-lg bg-cyan-400/15 border border-cyan-300/30 cq-px text-[7px] text-cyan-200">BETA</span>
      </div>

      {/* To-do list */}
      <div className="absolute top-16 left-3 z-20 w-52 rounded-xl bg-black/45 backdrop-blur border border-white/15 p-3" data-testid="world3d-todo">
        <p className="cq-px text-[9px] text-amber-300 mb-2">TO-DO LIST</p>
        <div className="space-y-1.5">
          {TODOS.map((t) => (
            <div key={t.todo} className="flex items-center gap-2" data-testid={`world3d-todo-${t.todo}`}>
              <span className={`w-4 h-4 rounded grid place-items-center text-[10px] border ${done[t.todo] ? "bg-emerald-400 border-emerald-300 text-slate-900" : "border-white/30 text-transparent"}`}>✓</span>
              <span className={`text-[13px] ${done[t.todo] ? "line-through text-slate-500" : "text-slate-100"}`}>{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Interaction prompt */}
      {near && !loading && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-28 sm:bottom-24 z-20" data-testid="world3d-prompt">
          <button onClick={() => worldRef.current?.tryInteract()} className="cq-px text-[10px] px-5 py-3 rounded-xl bg-amber-400 text-slate-950 shadow-[0_10px_30px_-8px_rgba(251,191,36,.8)] hover:bg-amber-300 inline-flex items-center gap-2 animate-pulse" data-testid="world3d-interact">
            <Hand size={14} /> {isTouch ? "TAP TO" : "PRESS E ·"} {actionText.toUpperCase()}
          </button>
        </div>
      )}

      {/* Hint (desktop) */}
      {!isTouch && !loading && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 text-center text-slate-400 text-[11px] pointer-events-none">WASD / arrows to move · drag to look · scroll to zoom · <span className="text-cyan-300">E</span> to interact</div>
      )}

      {/* Mobile joystick */}
      {isTouch && (
        <div ref={joyRef} onPointerDown={joyStart} onPointerMove={joyMove} onPointerUp={joyEnd} onPointerCancel={joyEnd}
          className="absolute bottom-8 left-6 z-20 w-32 h-32 rounded-full bg-white/10 border border-white/20 backdrop-blur touch-none" data-testid="world3d-joystick">
          <div className="absolute w-14 h-14 rounded-full bg-cyan-400/60 border border-cyan-200/70" style={{ left: "50%", top: "50%", transform: `translate(calc(-50% + ${knob.x}px), calc(-50% + ${knob.y}px))` }} />
        </div>
      )}
    </div>
  );
}
